// shared-lib/cabinet-runs/neighbourRuns.js
//
// What OTHER cabinet runs stand in a room, and how far a piece of one run can
// slide before it meets them.
//
// Two places ask, and they have to give the same answer: dragging a run end
// (web `runEndNeighbours.js` — how far the end may travel) and filling an
// island (`freestanding.js` — how much countertop lip an open end has room
// for). When only the drag knew about other runs, a scanned island stretched
// flush into a wall run was rebuilt with the standard 1" lip at that end, and
// the lip went an inch into the wall run's counter.
//
// "Another run" means what is actually standing: objects stamped with another
// run's id, plus the appliances its bays anchor (which carry no `runId`).
// Heights count — a wall run's uppers do not stand in the way of the island
// counter beneath them.

const {
  sweptDistance,
} = require('../bathroom-analyzer/pipeline/5.1-physicalCollisions');
const { FP_EPSILON } = require('../collision/tolerances');
const { footprintFor } = require('../geometry/objectFootprint');
const { runAnswersTo } = require('./runIds');
const { runBayForObject } = require('./bays');

/** An object's plan footprint and vertical span (null when unmeasured). */
function solidOf(o) {
  const { x, y, z } = o.dimensions;
  const cy = o.center.y;
  return {
    footprint: footprintFor(o.center, x, z, o.rotation?.y ?? 0),
    height:
      Number.isFinite(cy) && y > 0 ? { lo: cy - y / 2, hi: cy + y / 2 } : null,
  };
}

/** Do two vertical spans overlap? An unmeasured one overlaps everything. */
const sameHeight = (a, b) =>
  !a || !b || (a.lo < b.hi - FP_EPSILON && b.lo < a.hi - FP_EPSILON);

/**
 * Whose is this object: the run's (`'own'`), another run's (`'neighbour'`),
 * or no run's (null). A `runId` stamp says so outright; an appliance carries
 * none and belongs to the run whose bay anchors it — asked of THIS run first,
 * because a room can hold two runs whose bays both name the same appliance (a
 * scan run and the auto run of the same wall, on the corpus), and the
 * room-wide lookup answers whichever comes first.
 */
function ownerOf(room, run, o) {
  if (o.runId) return runAnswersTo(run, o.runId) ? 'own' : 'neighbour';
  if (runBayForObject({ cabinetRuns: [run] }, o)) return 'own';
  return runBayForObject(room, o) ? 'neighbour' : null;
}

/**
 * The room's objects split by run: what `run` stands (`own`) and what every
 * OTHER run stands (`neighbours`).
 *
 * @param {Object} room
 * @param {Object} run  stored or rehydrated — ownership is alias-tolerant
 * @returns {{own: Array, neighbours: Array}} `{footprint, height}` solids
 */
function runSolidsByOwner(room, run) {
  const own = [];
  const neighbours = [];
  for (const o of room?.objects || []) {
    const dims = o?.dimensions;
    if (!o?.center || !(dims?.x > 0) || !(dims?.z >= 0)) continue;
    const owner = ownerOf(room, run, o);
    if (owner === 'own') own.push(solidOf(o));
    else if (owner === 'neighbour') neighbours.push(solidOf(o));
  }
  return { own, neighbours };
}

/**
 * The solids with some part beyond `point` in direction `dir` — the only ones
 * that can stop something moving that way from there. Anything wholly behind
 * is ignored even where it already overlaps: a scanned island's counter is one
 * object the length of the island, and where the island butts into a wall run
 * it overlaps that run already, which says nothing about its other end.
 */
function solidsAhead(solids, point, dir) {
  return solids.filter((s) =>
    s.footprint.some(
      (p) => (p.x - point.x) * dir.x + (p.z - point.z) * dir.z > FP_EPSILON
    )
  );
}

/**
 * How far `mover` may slide along unit `dir` before it touches one of
 * `solids` at its own height — 0 when it already does, Infinity when nothing
 * is in its path. The shared SAT sweep (`sweptDistance`).
 *
 * @param {{footprint: Array, height: Object|null}} mover
 * @param {{x: number, z: number}} dir
 * @param {Array} solids
 * @returns {number}
 */
function clearAlong(mover, dir, solids) {
  const obstacles = solids
    .filter((s) => sameHeight(s.height, mover.height))
    .map((s) => s.footprint);
  if (!obstacles.length) return Infinity;
  return sweptDistance(mover.footprint, dir, obstacles).distance;
}

module.exports = {
  solidOf,
  runSolidsByOwner,
  solidsAhead,
  clearAlong,
};
